import { cn } from "@/lib/cn";
import { InstallPrompt } from "@/components/InstallPrompt";
import { TopBar } from "./TopBar";
import { BottomTabBar } from "./BottomTabBar";
import { NavRail } from "./NavRail";

export interface AppShellProps {
  children: React.ReactNode;
  /** Actions surfaced in the mobile top bar and the desktop rail footer. */
  actions?: React.ReactNode;
  /** Sub-screen mode: the mobile top bar shows a back arrow + this title. */
  title?: string;
  /** Back fallback when there is no history (see TopBar). */
  backHref?: string;
  /** Constrain content width. Defaults to a comfortable reading measure. */
  maxWidth?: string;
  /**
   * App-screen mode: the shell is locked to exactly the visual viewport
   * (h-dvh) and content scrolls INSIDE <main> instead of the page body.
   * Use for screens that must always fit the screen on mobile (deck,
   * matches). Content pages (settings, profile edit) keep the default
   * body scroll.
   */
  fit?: boolean;
  className?: string;
}

/**
 * App layout wrapper. Mobile: sticky TopBar + scrollable content + bottom
 * BottomTabBar (fixed on scrolling pages, in-flow in fit mode). Desktop
 * (md+): a slim left NavRail with the content offset to its right. No
 * routing or data — screens pass their own content.
 */
export function AppShell({
  children,
  actions,
  title,
  backHref,
  maxWidth = "max-w-2xl",
  fit = false,
  className,
}: AppShellProps) {
  return (
    <div
      className={cn(
        "md:pl-64",
        // fit height comes from --app-h (AppHeightSync): bare 100dvh resolves
        // short on iOS PWA launch/relaunch until WebKit's silent correction.
        fit ? "flex h-[var(--app-h,100dvh)] flex-col" : "min-h-dvh",
      )}
    >
      <NavRail actions={actions} />
      <TopBar actions={actions} title={title} backHref={backHref} />

      <main
        className={cn(
          "px-4 pt-5 sm:px-6 md:pt-8 md:pb-12",
          fit
            ? // fit: main becomes the scroller, contained so the body never
              // bounces; the tab bar sits in-flow below, so no clearance pad
              "min-h-0 flex-1 overflow-y-auto overscroll-contain pb-5"
            : // bottom padding clears the fixed mobile tab bar
              "pb-[calc(5rem+env(safe-area-inset-bottom))]",
          className,
        )}
      >
        <div className={cn("mx-auto w-full", maxWidth, fit && "flex min-h-full flex-col")}>
          {children}
        </div>
      </main>

      {/* fit: in-flow (immune to iOS stale-fixed-anchor gap); else fixed */}
      <BottomTabBar inFlow={fit} />
      {/* Single mount point for the PWA install prompt — logged-in surfaces
          only. Remove this line when the native iOS app ships (see
          InstallPrompt.tsx removal note). */}
      <InstallPrompt />
    </div>
  );
}
